import { useFrame, useThree } from '@react-three/fiber';
import { useCallback, useEffect, useMemo } from 'react';
import { type BatchedMesh, Color, Matrix4, MeshBasicMaterial, Object3D } from 'three';

import { LayerType } from 'app/modules/wallforming/components/scenes/camera-controls/types/layerType';
import { usePoiBatchedMeshHandler } from 'app/modules/wallforming/components/scenes/objects/pois/hooks/usePoiBatchedMeshHandler';
import { PoiGeometryType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiMeshType';
import { PoiPointEntry } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiPointEntry';
import {
    applyPoiPointTransform,
    createPoiGeometryByType,
    updatePoiBatchedMatrices
} from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';

import type { PoiBatchedEntry } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiBatchedEntry';

const poiMaterial = new MeshBasicMaterial({
    depthTest: false,
    transparent: true
});

const selectionColor = new Color('#649fda');
const dummyMesh = new Object3D();
const matrixScratch = new Matrix4();
const pointsGeometryByType = createPoiGeometryByType(PoiGeometryType.POINTS);

interface PoiPointsProps {
    entries: PoiPointEntry[];
}

export const PoiPoints = ({ entries }: PoiPointsProps) => {
    const camera = useThree((state) => state.camera);

    const batchedEntries = useMemo<PoiBatchedEntry[]>(
        () =>
            entries.map((entry) => ({
                poiIndex: entry.poiIndex,
                matrix: new Matrix4(),
                defaultColor: entry.defaultColor,
                hoverColor: entry.hoverColor,
                selectedColor: selectionColor,
                filterType: entry.filterType
            })),
        [entries]
    );

    const {
        meshRef,
        handleMeshRef: handleBatchedMeshRef,
        handleOnPoiSelection,
        handleOnPointerMove,
        handleOnPointerLeave,
        instanceIdByEntryIndexRef
    } = usePoiBatchedMeshHandler({ entries: batchedEntries, geometriesByType: pointsGeometryByType });

    const applyMatrix = useCallback(
        (entry: PoiPointEntry, matrix: Matrix4) => {
            applyPoiPointTransform(dummyMesh, entry, camera.zoom);
            dummyMesh.updateMatrix();
            matrix.copy(dummyMesh.matrix);
        },
        [camera.zoom]
    );

    const updatePointMatrices = useCallback(() => {
        const mesh = meshRef.current;
        if (mesh) {
            updatePoiBatchedMatrices(mesh, entries, instanceIdByEntryIndexRef.current, applyMatrix, matrixScratch);
        }
    }, [applyMatrix, entries, instanceIdByEntryIndexRef, meshRef]);

    useFrame(() => {
        updatePointMatrices();
    });

    useEffect(() => {
        updatePointMatrices();
    }, [entries, updatePointMatrices]);

    const handlePoiMeshRef = useCallback(
        (mesh: BatchedMesh | null) => {
            handleBatchedMeshRef(mesh);
            if (!mesh) {
                return;
            }
            updatePointMatrices();
        },
        [handleBatchedMeshRef, updatePointMatrices]
    );

    useEffect(() => {
        return () => poiMaterial?.dispose();
    }, []);

    if (entries.length === 0) {
        return null;
    }

    return (
        <batchedMesh
            ref={handlePoiMeshRef}
            args={[40_000, 40_000, 40_000, poiMaterial]}
            layers={[LayerType.RAYCAST]}
            name="poi-interaction-target"
            perObjectFrustumCulled={true}
            renderOrder={100}
            onPointerDown={handleOnPoiSelection}
            onPointerLeave={handleOnPointerLeave}
            onPointerMove={handleOnPointerMove}
        />
    );
};
